/**
 * Mapping of Tomorrow.io weather codes to their actual icon file names
 * Files are in format: {code}_{description}_large.png
 * 
 * Includes both:
 * - 4-digit "simple/fullDay" codes (returned by daily 1d timestep)
 * - 5-digit "day/night" codes (returned by hourly 1h timestep)
 */
export const WEATHER_ICON_FILE_MAP: Record<string, string> = {
  // ===========================================
  // 4-digit Simple/Full Day Codes (daily API)
  // ===========================================
  // These codes are returned when using 1d timestep
  // Map them to their equivalent day icons
  
  // Clear/Cloudy
  '1000': '10000_clear_large.png',
  '1100': '11000_mostly_clear_large.png',
  '1101': '11010_partly_cloudy_large.png',
  '1102': '11020_mostly_cloudy_large.png',
  '1001': '10010_cloudy_large.png',
  '1103': '11030_mostly_clear_large.png',
  
  // Fog
  '2000': '20000_fog_large.png',
  '2100': '21000_fog_light_large.png',
  '2101': '21010_fog_light_mostly_clear_large.png',
  '2102': '21020_fog_light_partly_cloudy_large.png',
  '2103': '21030_fog_light_mostly_cloudy_large.png',
  '2106': '21060_fog_mostly_clear_large.png',
  '2107': '21070_fog_partly_cloudy_large.png',
  '2108': '21080_fog_mostly_cloudy_large.png',
  
  // Drizzle & Rain
  '4000': '40000_drizzle_large.png',
  '4001': '40010_rain_large.png',
  '4200': '42000_rain_light_large.png',
  '4201': '42010_rain_heavy_large.png',
  '4202': '42020_rain_heavy_partly_cloudy_large.png',
  '4203': '42030_drizzle_mostly_clear_large.png',
  '4204': '42040_drizzle_partly_cloudy_large.png',
  '4205': '42050_drizzle_mostly_cloudy_large.png',
  '4208': '42080_rain_partly_cloudy_large.png',
  '4209': '42090_rain_mostly_clear_large.png',
  '4210': '42100_rain_mostly_cloudy_large.png',
  '4211': '42110_rain_heavy_mostly_clear_large.png',
  '4212': '42120_rain_heavy_mostly_cloudy_large.png',
  '4213': '42130_rain_light_mostly_clear_large.png',
  '4214': '42140_rain_light_partly_cloudy_large.png',
  '4215': '42150_rain_light_mostly_cloudy_large.png',
  
  // Snow
  '5000': '50000_snow_large.png',
  '5001': '50010_flurries_large.png',
  '5100': '51000_snow_light_large.png',
  '5101': '51010_snow_heavy_large.png',
  '5102': '51020_snow_light_mostly_clear_large.png',
  '5103': '51030_snow_light_partly_cloudy_large.png',
  '5104': '51040_snow_light_mostly_cloudy_large.png',
  '5105': '51050_snow_mostly_clear_large.png',
  '5106': '51060_snow_partly_cloudy_large.png',
  '5107': '51070_snow_mostly_cloudy_large.png',
  '5108': '51080_wintry_mix_large.png',
  '5110': '51100_wintry_mix_large.png',
  '5112': '51120_wintry_mix_large.png',
  '5114': '51140_wintry_mix_large.png',
  '5115': '51150_flurries_mostly_clear_large.png',
  '5116': '51160_flurries_partly_cloudy_large.png',
  '5117': '51170_flurries_mostly_cloudy_large.png',
  '5119': '51190_snow_heavy_mostly_clear_large.png',
  '5120': '51200_snow_heavy_partly_cloudy_large.png',
  '5121': '51210_snow_heavy_mostly_cloudy_large.png',
  '5122': '51220_wintry_mix_large.png',
  
  // Freezing Rain
  '6000': '60000_freezing_rain_drizzle_large.png',
  '6001': '60010_freezing_rain_large.png',
  '6002': '60020_freezing_rain_drizzle_partly_cloudy_large.png',
  '6003': '60030_freezing_rain_drizzle_mostly_clear_large.png',
  '6004': '60040_freezing_rain_drizzle_mostly_cloudy_large.png',
  '6200': '62000_freezing_rain_light_large.png',
  '6201': '62010_freezing_rain_heavy_large.png',
  '6202': '62020_freezing_rain_heavy_partly_cloudy_large.png',
  '6203': '62030_freezing_rain_light_partly_cloudy_large.png',
  '6204': '62040_wintry_mix_large.png',
  '6205': '62050_freezing_rain_light_mostly_clear_large.png',
  '6206': '62060_wintry_mix_large.png',
  '6207': '62070_freezing_rain_heavy_mostly_clear_large.png',
  '6208': '62080_freezing_rain_heavy_mostly_cloudy_large.png',
  '6209': '62090_freezing_rain_light_mostly_cloudy_large.png',
  '6212': '62120_wintry_mix_large.png',
  '6213': '62130_freezing_rain_mostly_clear_large.png',
  '6214': '62140_freezing_rain_partly_cloudy_large.png',
  '6215': '62150_freezing_rain_mostly_cloudy_large.png',
  '6220': '62200_wintry_mix_large.png',
  '6222': '62220_wintry_mix_large.png',
  
  // Ice Pellets
  '7000': '70000_ice_pellets_large.png',
  '7101': '71010_ice_pellets_heavy_large.png',
  '7102': '71020_ice_pellets_light_large.png',
  '7103': '71030_wintry_mix_large.png',
  '7105': '71050_wintry_mix_large.png',
  '7106': '71060_wintry_mix_large.png',
  '7107': '71070_ice_pellets_partly_cloudy_large.png',
  '7108': '71080_ice_pellets_mostly_clear_large.png',
  '7109': '71090_ice_pellets_mostly_cloudy_large.png',
  '7110': '71100_ice_pellets_light_mostly_clear_large.png',
  '7111': '71110_ice_pellets_light_partly_cloudy_large.png',
  '7112': '71120_ice_pellets_light_mostly_cloudy_large.png',
  '7113': '71130_ice_pellets_heavy_mostly_clear_large.png',
  '7114': '71140_ice_pellets_heavy_partly_cloudy_large.png',
  '7115': '71150_wintry_mix_large.png',
  '7116': '71160_ice_pellets_heavy_mostly_cloudy_large.png',
  '7117': '71170_wintry_mix_large.png',
  
  // Thunderstorm
  '8000': '80000_tstorm_large.png',
  '8001': '80010_tstorm_mostly_clear_large.png',
  '8002': '80020_tstorm_mostly_cloudy_large.png',
  '8003': '80030_tstorm_partly_cloudy_large.png',
  
  // ===========================================
  // 5-digit Day/Night Codes (hourly API)
  // ===========================================
  // These codes are returned when using 1h timestep
  
  // Clear
  '10000': '10000_clear_large.png',
  '10001': '10001_clear_large.png',
  
  // Cloudy
  '10010': '10010_cloudy_large.png',
  '10011': '10010_cloudy_large.png',  // Night - Cloudy (uses day icon, no night variant exists)
  '11000': '11000_mostly_clear_large.png',
  '11001': '11001_mostly_clear_large.png',
  '11010': '11010_partly_cloudy_large.png',
  '11011': '11011_partly_cloudy_large.png',
  '11020': '11020_mostly_cloudy_large.png',
  '11021': '11021_mostly_cloudy_large.png',
  '11030': '11030_mostly_clear_large.png',
  '11031': '11031_mostly_clear_large.png',
  
  // Fog
  '20000': '20000_fog_large.png',
  '20001': '20000_fog_large.png',  // Night - Fog (uses day icon)
  '21000': '21000_fog_light_large.png',
  '21001': '21000_fog_light_large.png',  // Night - Light Fog (uses day icon)
  '21010': '21010_fog_light_mostly_clear_large.png',
  '21011': '21011_fog_light_mostly_clear_large.png',
  '21020': '21020_fog_light_partly_cloudy_large.png',
  '21021': '21021_fog_light_partly_cloudy_large.png',
  '21030': '21030_fog_light_mostly_cloudy_large.png',
  '21031': '21031_fog_light_mostly_cloudy_large.png',
  '21060': '21060_fog_mostly_clear_large.png',
  '21061': '21061_fog_mostly_clear_large.png',
  '21070': '21070_fog_partly_cloudy_large.png',
  '21071': '21071_fog_partly_cloudy_large.png',
  '21080': '21080_fog_mostly_cloudy_large.png',
  '21081': '21081_fog_mostly_cloudy_large.png',
  
  // Drizzle & Rain
  '40000': '40000_drizzle_large.png',
  '40001': '40000_drizzle_large.png',  // Night - Drizzle (uses day icon)
  '40010': '40010_rain_large.png',
  '40011': '40010_rain_large.png',  // Night - Rain (uses day icon)
  '42000': '42000_rain_light_large.png',
  '42001': '42000_rain_light_large.png',  // Night - Light Rain (uses day icon)
  '42010': '42010_rain_heavy_large.png',
  '42011': '42010_rain_heavy_large.png',  // Night - Heavy Rain (uses day icon)
  '42020': '42020_rain_heavy_partly_cloudy_large.png',
  '42021': '42021_rain_heavy_partly_cloudy_large.png',
  '42030': '42030_drizzle_mostly_clear_large.png',
  '42031': '42031_drizzle_mostly_clear_large.png',
  '42040': '42040_drizzle_partly_cloudy_large.png',
  '42041': '42041_drizzle_partly_cloudy_large.png',
  '42050': '42050_drizzle_mostly_cloudy_large.png',
  '42051': '42051_drizzle_mostly_cloudy_large.png',
  '42080': '42080_rain_partly_cloudy_large.png',
  '42081': '42081_rain_partly_cloudy_large.png',
  '42090': '42090_rain_mostly_clear_large.png',
  '42091': '42091_rain_mostly_clear_large.png',
  '42100': '42100_rain_mostly_cloudy_large.png',
  '42101': '42101_rain_mostly_cloudy_large.png',
  '42110': '42110_rain_heavy_mostly_clear_large.png',
  '42111': '42111_rain_heavy_mostly_clear_large.png',
  '42120': '42120_rain_heavy_mostly_cloudy_large.png',
  '42121': '42121_rain_heavy_mostly_cloudy_large.png',
  '42130': '42130_rain_light_mostly_clear_large.png',
  '42131': '42131_rain_light_mostly_clear_large.png',
  '42140': '42140_rain_light_partly_cloudy_large.png',
  '42141': '42141_rain_light_partly_cloudy_large.png',
  '42150': '42150_rain_light_mostly_cloudy_large.png',
  '42151': '42151_rain_light_mostly_cloudy_large.png',
  
  // Snow
  '50000': '50000_snow_large.png',
  '50001': '50000_snow_large.png',  // Night - Snow (uses day icon)
  '50010': '50010_flurries_large.png',
  '50011': '50010_flurries_large.png',  // Night - Flurries (uses day icon)
  '51000': '51000_snow_light_large.png',
  '51001': '51000_snow_light_large.png',  // Night - Light Snow (uses day icon)
  '51010': '51010_snow_heavy_large.png',
  '51011': '51010_snow_heavy_large.png',  // Night - Heavy Snow (uses day icon)
  '51020': '51020_snow_light_mostly_clear_large.png',
  '51021': '51021_snow_light_mostly_clear_large.png',
  '51030': '51030_snow_light_partly_cloudy_large.png',
  '51031': '51031_snow_light_partly_cloudy_large.png',
  '51040': '51040_snow_light_mostly_cloudy_large.png',
  '51041': '51041_snow_light_mostly_cloudy_large.png',
  '51050': '51050_snow_mostly_clear_large.png',
  '51051': '51051_snow_mostly_clear_large.png',
  '51060': '51060_snow_partly_cloudy_large.png',
  '51061': '51061_snow_partly_cloudy_large.png',
  '51070': '51070_snow_mostly_cloudy_large.png',
  '51071': '51071_snow_mostly_cloudy_large.png',
  '51080': '51080_wintry_mix_large.png',
  '51081': '51080_wintry_mix_large.png',  // Night - Rain and Snow (uses day icon)
  '51100': '51100_wintry_mix_large.png',
  '51101': '51100_wintry_mix_large.png',  // Night - Drizzle and Snow (uses day icon)
  '51120': '51120_wintry_mix_large.png',
  '51121': '51120_wintry_mix_large.png',  // Night - Snow and Ice Pellets (uses day icon)
  '51140': '51140_wintry_mix_large.png',
  '51141': '51140_wintry_mix_large.png',  // Night - Snow and Freezing Rain (uses day icon)
  '51150': '51150_flurries_mostly_clear_large.png',
  '51151': '51151_flurries_mostly_clear_large.png',
  '51160': '51160_flurries_partly_cloudy_large.png',
  '51161': '51161_flurries_partly_cloudy_large.png',
  '51170': '51170_flurries_mostly_cloudy_large.png',
  '51171': '51171_flurries_mostly_cloudy_large.png',
  '51190': '51190_snow_heavy_mostly_clear_large.png',
  '51191': '51191_snow_heavy_mostly_clear_large.png',
  '51200': '51200_snow_heavy_partly_cloudy_large.png',
  '51201': '51201_snow_heavy_partly_cloudy_large.png',
  '51210': '51210_snow_heavy_mostly_cloudy_large.png',
  '51211': '51211_snow_heavy_mostly_cloudy_large.png',
  '51220': '51220_wintry_mix_large.png',
  '51221': '51220_wintry_mix_large.png',  // Night - Drizzle and Light Snow (uses day icon)

  // Freezing Rain
  '60000': '60000_freezing_rain_drizzle_large.png',
  '60001': '60000_freezing_rain_drizzle_large.png',  // Night - Freezing Drizzle (uses day icon)
  '60010': '60010_freezing_rain_large.png',
  '60011': '60010_freezing_rain_large.png',  // Night - Freezing Rain (uses day icon)
  '60020': '60020_freezing_rain_drizzle_partly_cloudy_large.png',
  '60021': '60021_freezing_rain_drizzle_partly_cloudy_large.png',
  '60030': '60030_freezing_rain_drizzle_mostly_clear_large.png',
  '60031': '60031_freezing_rain_drizzle_mostly_clear_large.png',
  '60040': '60040_freezing_rain_drizzle_mostly_cloudy_large.png',
  '60041': '60041_freezing_rain_drizzle_mostly_cloudy_large.png',
  '62000': '62000_freezing_rain_light_large.png',
  '62001': '62000_freezing_rain_light_large.png',  // Night - Light Freezing Rain (uses day icon)
  '62010': '62010_freezing_rain_heavy_large.png',
  '62011': '62010_freezing_rain_heavy_large.png',  // Night - Heavy Freezing Rain (uses day icon)
  '62020': '62020_freezing_rain_heavy_partly_cloudy_large.png',
  '62021': '62021_freezing_rain_heavy_partly_cloudy_large.png',
  '62030': '62030_freezing_rain_light_partly_cloudy_large.png',
  '62031': '62031_freezing_rain_light_partly_cloudy_large.png',
  '62040': '62040_wintry_mix_large.png',
  '62041': '62040_wintry_mix_large.png',  // Night - Drizzle and Freezing Drizzle (uses day icon)
  '62050': '62050_freezing_rain_light_mostly_clear_large.png',
  '62051': '62051_freezing_rain_light_mostly_clear_large.png',
  '62060': '62060_wintry_mix_large.png',
  '62061': '62060_wintry_mix_large.png',  // Night - Light Rain and Freezing Drizzle (uses day icon)
  '62070': '62070_freezing_rain_heavy_mostly_clear_large.png',
  '62071': '62071_freezing_rain_heavy_mostly_clear_large.png',
  '62080': '62080_freezing_rain_heavy_mostly_cloudy_large.png',
  '62081': '62081_freezing_rain_heavy_mostly_cloudy_large.png',
  '62090': '62090_freezing_rain_light_mostly_cloudy_large.png',
  '62091': '62091_freezing_rain_light_mostly_cloudy_large.png',
  '62120': '62120_wintry_mix_large.png',
  '62121': '62120_wintry_mix_large.png',  // Night - Drizzle and Freezing Rain (uses day icon)
  '62130': '62130_freezing_rain_mostly_clear_large.png',
  '62131': '62131_freezing_rain_mostly_clear_large.png',
  '62140': '62140_freezing_rain_partly_cloudy_large.png',
  '62141': '62141_freezing_rain_partly_cloudy_large.png',
  '62150': '62150_freezing_rain_mostly_cloudy_large.png',
  '62151': '62151_freezing_rain_mostly_cloudy_large.png',
  '62200': '62200_wintry_mix_large.png',
  '62201': '62200_wintry_mix_large.png',  // Night - Light Rain and Freezing Rain (uses day icon)
  '62220': '62220_wintry_mix_large.png',
  '62221': '62220_wintry_mix_large.png',  // Night - Rain and Freezing Rain (uses day icon)

  // Ice Pellets
  '70000': '70000_ice_pellets_large.png',
  '70001': '70000_ice_pellets_large.png',  // Night - Ice Pellets (uses day icon)
  '71010': '71010_ice_pellets_heavy_large.png',
  '71011': '71010_ice_pellets_heavy_large.png',  // Night - Heavy Ice Pellets (uses day icon)
  '71020': '71020_ice_pellets_light_large.png',
  '71021': '71020_ice_pellets_light_large.png',  // Night - Light Ice Pellets (uses day icon)
  '71030': '71030_wintry_mix_large.png',
  '71031': '71030_wintry_mix_large.png',  // Night - Freezing Rain and Heavy Ice Pellets (uses day icon)
  '71050': '71050_wintry_mix_large.png',
  '71051': '71050_wintry_mix_large.png',  // Night - Drizzle and Ice Pellets (uses day icon)
  '71060': '71060_wintry_mix_large.png',
  '71061': '71060_wintry_mix_large.png',  // Night - Freezing Rain and Ice Pellets (uses day icon)
  '71070': '71070_ice_pellets_partly_cloudy_large.png',
  '71071': '71071_ice_pellets_partly_cloudy_large.png',
  '71080': '71080_ice_pellets_mostly_clear_large.png',
  '71081': '71081_ice_pellets_mostly_clear_large.png',
  '71090': '71090_ice_pellets_mostly_cloudy_large.png',
  '71091': '71091_ice_pellets_mostly_cloudy_large.png',
  '71100': '71100_ice_pellets_light_mostly_clear_large.png',
  '71101': '71101_ice_pellets_light_mostly_clear_large.png',
  '71110': '71110_ice_pellets_light_partly_cloudy_large.png',
  '71111': '71111_ice_pellets_light_partly_cloudy_large.png',
  '71120': '71120_ice_pellets_light_mostly_cloudy_large.png',
  '71121': '71121_ice_pellets_light_mostly_cloudy_large.png',
  '71130': '71130_ice_pellets_heavy_mostly_clear_large.png',
  '71131': '71131_ice_pellets_heavy_mostly_clear_large.png',
  '71140': '71140_ice_pellets_heavy_partly_cloudy_large.png',
  '71141': '71141_ice_pellets_heavy_partly_cloudy_large.png',
  '71150': '71150_wintry_mix_large.png',
  '71151': '71150_wintry_mix_large.png',  // Night - Light Rain and Ice Pellets (uses day icon)
  '71160': '71160_ice_pellets_heavy_mostly_cloudy_large.png',
  '71161': '71161_ice_pellets_heavy_mostly_cloudy_large.png',
  '71170': '71170_wintry_mix_large.png',
  '71171': '71170_wintry_mix_large.png',  // Night - Rain and Ice Pellets (uses day icon)

  // Thunderstorm
  '80000': '80000_tstorm_large.png',
  '80001': '80000_tstorm_large.png',  // Night - Thunderstorm (uses day icon)
  '80010': '80010_tstorm_mostly_clear_large.png',
  '80011': '80011_tstorm_mostly_clear_large.png',
  '80020': '80020_tstorm_mostly_cloudy_large.png',
  '80021': '80021_tstorm_mostly_cloudy_large.png',
  '80030': '80030_tstorm_partly_cloudy_large.png',
  '80031': '80031_tstorm_partly_cloudy_large.png',
};

/**
 * Get the actual icon file name for a weather code
 */
export function getWeatherIconFileName(code: string): string {
  return WEATHER_ICON_FILE_MAP[code] || `${code}_large.png`; // Fallback to simple format
}

